// Small client for the CloudPhone API (api2.).
// Every request sends the session cookie; POST/PATCH/DELETE also send the
// CSRF header the backend requires (X-CloudPhone: 1).

import { apiUrl } from './config';
import { deviceZone } from './time';

export interface User {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'user';
  status: 'active' | 'disabled';
  hasPhone: boolean;
  sipUsername: string | null;
  /** ring every signed-in device for incoming calls */
  ringAll?: boolean;
  /** outbound caller ID choice: one of their numbers, 'rotate', or null (automatic) */
  callerId?: string | null;
  /** Do Not Disturb: incoming calls go straight to voicemail */
  dnd?: boolean;
  /** call forwarding: off, always, or when not answered; the number is kept when off */
  forwardMode?: ForwardMode;
  forwardNumber?: string | null;
  /** two-step login is set up (admin PR 2) */
  twoStep?: boolean;
  /** still has the temporary password an admin gave (admin PR 3a) */
  mustSetPassword?: boolean;
  tempPasswordUntil?: string | null;
  /** waiting for an invitation to be accepted (admin PR 3b); the link works until inviteUntil */
  invited?: boolean;
  inviteUntil?: string | null;
  /** time-zone PR: the zone picked in Settings (IANA name); null = "Automatic (this device)" */
  timeZone?: string | null;
  /** the zone this person's browser last reported (shown to admins for Automatic users) */
  deviceTimeZone?: string | null;
  /** PR 1a: the plan (name as when it was assigned); null = no plan yet */
  plan?: { id: number; code: string; name: string } | null;
  /** a cheaper plan waiting for the next 1st ('YYYY-MM-DD') */
  nextPlan?: { id: number; code: string; name: string; from: string } | null;
  createdAt: string;
  lastLoginAt: string | null;
}

/** Outbound caller ID ("Calling from"): the user's numbers and what calls show. */
export interface CallerIdInfo {
  /** the user's numbers (DIDs), digits */
  numbers: string[];
  /** their choice: a number, 'rotate', or null (automatic) */
  selected: string | null;
  /** what calls show now: one number, each call the next number, or the account's own (no numbers) */
  mode: 'number' | 'rotate' | 'none';
  number: string | null;
  /** the server can apply the choice per call (dial cpout<number> + X-CP-CallerID) */
  ready: boolean;
  /** Numbers PR 2: the user's names for their numbers ({ number → name }) */
  names?: Record<string, string>;
}

export type ForwardMode = 'off' | 'always' | 'noanswer';

/** Call forwarding to the user's mobile. */
export interface ForwardInfo {
  mode: ForwardMode;
  /** digits, 1 + 10 */
  number: string | null;
  /** the server supports forwarding */
  available: boolean;
  /** set up on every one of the user's numbers and working now */
  active: boolean;
  /** why it isn't working, if something is wrong ('' otherwise) */
  problem: string;
}

export interface SoftphoneConfig {
  wssUrl: string;
  sipUri: string;
  authorizationUser: string;
  password: string;
  displayName: string;
  iceServers: RTCIceServer[];
  iceExpiresAt: string | null;
  /** "Ring all my devices" is on: Decline sends 603 so every device stops */
  ringAll?: boolean;
  callerId?: CallerIdInfo;
  dnd?: boolean;
  forward?: ForwardInfo;
  transfer?: TransferInfo;
  /** number rules: prefixes after the leading 1 that can't be called (older servers: absent) */
  numberRules?: { blockedPrefixes: string[] };
}

/** Call transfer: what this account can do now. */
export interface TransferInfo {
  /** blind / warm transfer can be used */
  available: boolean;
  /** outgoing calls are placed by the server (dial cpout<number>), so they can be transferred too */
  serverCalls: boolean;
  /** conference calls can be used (add people, merge, people list) */
  conference?: boolean;
}

/** One person in a conference call (from the server). */
export interface Participant {
  /** id for remove / mute */
  id: string;
  /** their number ('' for the user) */
  number: string;
  state: 'ringing' | 'joined';
  /** muted for everyone by the user */
  muted: boolean;
  you: boolean;
  /** the call is being transferred to them: the user leaves when they join */
  transfer?: true;
}

export type CallReason =
  | 'declined'
  | 'no_answer'
  | 'caller_hung_up'
  | 'offline'
  | 'not_put_through'
  | 'dnd'
  | 'forwarded'
  | 'unreachable'
  | 'busy'
  | 'cancelled'
  | 'blocked'
  | 'paused'
  | 'no_balance'
  | 'failed';

export interface CallEntry {
  id: string;
  status: 'answered' | 'missed' | 'failed';
  /** why it wasn't answered; null when unknown (older calls from MagnusBilling) */
  reason: CallReason | null;
  direction: 'outbound' | 'inbound';
  number: string;
  /** caller name, when the caller sent one */
  name: string;
  callerId: string;
  /** outbound: the user's own number the call went out from ('' when unknown) */
  fromNumber?: string;
  /** inbound: the user's own number the caller dialled (absent when unknown; Numbers PR 1) */
  toNumber?: string;
  /** a voicemail message the caller left on this call */
  voicemail?: { id: number; durationSec: number; heard: boolean } | null;
  /** inbound: forwarded to this number ('' / missing = not forwarded) */
  forwardedTo?: string;
  /** the forward number answered (status 'answered', durationSec = its talk time) */
  forwardAnswered?: boolean;
  /** the user transferred this call to this number */
  transferredTo?: string;
  transferKind?: 'blind' | 'warm' | 'conference';
  /** blind: the target answered (false = it came back to the user; null = not known yet) */
  transferAnswered?: boolean | null;
  /** conference call: the people the user added who joined (also when it was then transferred) */
  conferenceWith?: string[];
  startedAt: string;
  durationSec: number;
  cost: number | null;
  hangupCause: number | null;
}

export interface CallsPage {
  calls: CallEntry[];
  nextBefore: number | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** the whole error answer (PR 1a: a changed plan summary comes back in it) */
    readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | null = null;

/** Called whenever the API says the session is gone (401). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export async function api<T>(
  path: string,
  opts: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; signal?: AbortSignal; quiet401?: boolean } = {},
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['X-CloudPhone'] = '1';
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(apiUrl + path, {
      method,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal ?? AbortSignal.timeout(15_000),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Can’t reach CloudPhone. Check your internet connection.');
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const body = (data ?? {}) as { error?: string; message?: string };
    if (res.status === 401 && !opts.quiet401) onUnauthorized?.();
    throw new ApiError(res.status, body.error ?? `http_${res.status}`, body.message ?? `Request failed (HTTP ${res.status})`, data);
  }
  return data as T;
}

// ?tz= tells the server this browser's zone, so admins can see it for "Automatic" users
export const getMe = () => api<{ user: User }>(`/v1/me?tz=${encodeURIComponent(deviceZone())}`, { quiet401: true }).then((r) => r.user);
/**
 * Login: done (a user), an admin's next step (the two-step code, or setting it up first),
 * or — after a temporary password an admin gave — "Choose your password" first (admin PR 3a).
 */
export type LoginResult =
  | { kind: 'done'; user: User }
  | { kind: 'two-step'; mode: 'code' | 'setup'; ticket: string }
  | { kind: 'new-password'; ticket: string };

type LoginAnswer = { user?: User; twoStep?: 'code' | 'setup'; newPassword?: boolean; ticket?: string };
const loginResult = (r: LoginAnswer): LoginResult =>
  r.newPassword && r.ticket
    ? { kind: 'new-password', ticket: r.ticket }
    : r.twoStep && r.ticket
      ? { kind: 'two-step', mode: r.twoStep, ticket: r.ticket }
      : { kind: 'done', user: r.user! };

export const login = (email: string, password: string, turnstile?: string): Promise<LoginResult> =>
  api<LoginAnswer>('/v1/auth/login', { method: 'POST', body: { email, password, turnstile }, quiet401: true }).then(loginResult);

// ── Invitations (admin PR 3b) ──

export interface InviteCheck {
  ok: true;
  email: string;
  name: string;
  /** an admin goes on to set up two-step login after choosing the password */
  admin: boolean;
  until: string | null;
}

/** Is the invitation link good? (the page shows the same error for every bad link) */
export const checkInvite = (email: string, token: string, turnstile?: string) =>
  api<InviteCheck>('/v1/auth/invite/check', { method: 'POST', body: { email, token, turnstile }, quiet401: true });

/** Choose the password from an invitation; then the login goes on (two-step setup for admins). */
export const acceptInvite = (email: string, token: string, password: string, turnstile?: string): Promise<LoginResult> =>
  api<LoginAnswer>('/v1/auth/invite/accept', { method: 'POST', body: { email, token, password, turnstile }, quiet401: true }).then(loginResult);

/** "Choose your password" after a temporary one; then the login goes on (two-step for admins). */
export const firstPassword = (ticket: string, password: string): Promise<LoginResult> =>
  api<LoginAnswer>('/v1/auth/first-password', { method: 'POST', body: { ticket, password }, quiet401: true }).then(loginResult);

// ── Two-step login (admin PR 2) ──

/** What the authenticator app needs: the QR code holds `uri`; `secretGrouped` is for typing it in. */
export interface TwoStepSecret {
  secret: string;
  secretGrouped: string;
  uri: string;
  account: string;
  issuer: string;
}

export interface TwoStepStatus {
  on: boolean;
  required: boolean;
  since: string | null;
  backupCodesLeft: number;
  pausedSeconds: number;
}

/** The code step of an admin login: a 6-digit app code or a backup code. */
export const twoStepLogin = (ticket: string, code: string) =>
  api<{ user: User; backupCodesLeft?: number }>('/v1/auth/two-step', { method: 'POST', body: { ticket, code }, quiet401: true });
export const twoStepSetupStart = (ticket: string) =>
  api<TwoStepSecret>('/v1/auth/two-step/setup', { method: 'POST', body: { ticket }, quiet401: true });
export const twoStepSetupFinish = (ticket: string, code: string) =>
  api<{ user: User; backupCodes: string[] }>('/v1/auth/two-step/setup/finish', { method: 'POST', body: { ticket, code }, quiet401: true });
export const getTwoStep = () => api<{ twoStep: TwoStepStatus }>('/v1/me/two-step').then((r) => r.twoStep);
export const newBackupCodes = (code: string) =>
  api<{ backupCodes: string[]; twoStep: TwoStepStatus }>('/v1/me/two-step/backup-codes', { method: 'POST', body: { code } });
export const resetupStart = (code: string) => api<TwoStepSecret>('/v1/me/two-step/setup', { method: 'POST', body: { code } });
export const resetupFinish = (code: string) =>
  api<{ twoStep: TwoStepStatus }>('/v1/me/two-step/setup/finish', { method: 'POST', body: { code } }).then((r) => r.twoStep);
export const logout = () => api<{ ok: true }>('/v1/auth/logout', { method: 'POST', body: {} });
export interface Voicemail {
  id: number;
  callId: string;
  from: string;
  name: string;
  /** the user's own number the caller dialled ('' when unknown; Numbers PR 1) */
  to?: string;
  durationSec: number;
  heard: boolean;
  createdAt: string;
}

export const getVoicemails = (before?: number | null, limit = 50) =>
  api<{ voicemails: Voicemail[]; unheard: number; nextBefore: number | null }>(
    `/v1/voicemails?limit=${limit}${before ? `&before=${before}` : ''}`,
  );
export const markVoicemailHeard = (id: number, heard = true) =>
  api<{ ok: true }>(`/v1/voicemails/${id}`, { method: 'PATCH', body: { heard } });
export const deleteVoicemail = (id: number) => api<{ ok: true }>(`/v1/voicemails/${id}`, { method: 'DELETE' });

/** The recording as a blob (sent with the session cookie). */
export async function voicemailAudio(id: number): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${apiUrl}/v1/voicemails/${id}/audio`, { credentials: 'include', signal: AbortSignal.timeout(30_000) });
  } catch {
    throw new ApiError(0, 'network', 'Can’t reach CloudPhone. Check your internet connection.');
  }
  if (res.status === 401) onUnauthorized?.();
  if (!res.ok) throw new ApiError(res.status, `http_${res.status}`, res.status === 404 ? 'This voicemail is gone.' : 'Couldn’t load the voicemail.');
  return res.blob();
}

export const saveSettings = (settings: {
  ringAll?: boolean;
  callerId?: string | null;
  dnd?: boolean;
  forwardMode?: ForwardMode;
  forwardNumber?: string | null;
  /** an IANA zone, or null = Automatic */
  timeZone?: string | null;
}) => api<{ user: User; callerId: CallerIdInfo; forward: ForwardInfo }>('/v1/me/settings', { method: 'PUT', body: settings });
/** Numbers PR 2: name one of your numbers (null or '' removes the name). */
export const saveNumberName = (number: string, nickname: string | null) =>
  api<{ number: string; nickname: string | null; callerId: CallerIdInfo }>(`/v1/me/numbers/${encodeURIComponent(number.replace(/[^0-9]/g, ''))}`, {
    method: 'PUT',
    body: { nickname },
  });
export const getSoftphoneConfig = () => api<SoftphoneConfig>('/v1/softphone-config');
export const getCalls = (before?: number | null, limit = 50) =>
  api<CallsPage>(`/v1/calls?limit=${limit}${before ? `&before=${before}` : ''}`);

/**
 * Blind transfer: the other person rings `to`; this device's call ends. A
 * conference: `to` is called into it and the user leaves when they answer
 * (the answer has the people list, `to` ringing).
 */
export const transferCall = (sipCallId: string, to: string) =>
  api<{ ok: true; to: string; participants?: Participant[]; version?: number }>('/v1/calls/transfer', { method: 'POST', body: { sipCallId, to } });
/** The held call (or conference) and the call to the target become one conference call. */
export const mergeCalls = (sipCallId: string, consultSipCallId: string) =>
  api<{ ok: true; with: string; participants: Participant[]; version: number }>('/v1/calls/merge', { method: 'POST', body: { sipCallId, consultSipCallId } });
/** "Add now": call `to` into this call; it becomes a conference. */
export const addToCall = (sipCallId: string, to: string) =>
  api<{ ok: true; to: string; participants: Participant[]; version: number }>('/v1/calls/conference/add', { method: 'POST', body: { sipCallId, to } });
export const removeFromCall = (sipCallId: string, participant: string) =>
  api<{ ok: true; participants: Participant[]; version: number }>('/v1/calls/conference/remove', { method: 'POST', body: { sipCallId, participant } });
export const muteInCall = (sipCallId: string, participant: string, muted: boolean) =>
  api<{ ok: true; participants: Participant[]; version: number }>('/v1/calls/conference/mute', { method: 'POST', body: { sipCallId, participant, muted } });
/** End the conference for everyone. */
/** Hang up on the server a call this device lost (its BYE may not have arrived). */
export const hangupLostCall = (sipCallId: string) => api<{ ok: true }>('/v1/calls/hangup', { method: 'POST', body: { sipCallId } });
export const endCallForEveryone = (sipCallId: string) =>
  api<{ ok: true }>('/v1/calls/conference/end', { method: 'POST', body: { sipCallId } });
/** Warm transfer: join the held call with the call to the target; both of the user's calls end. */
export const completeTransfer = (sipCallId: string, consultSipCallId: string) =>
  api<{ ok: true; to: string }>('/v1/calls/transfer/complete', { method: 'POST', body: { sipCallId, consultSipCallId } });

// ── Login options, forgot / change password ──

export interface AuthOptions {
  /** "Forgot password?" works (email is set up on the server) */
  passwordReset: boolean;
  /** Cloudflare Turnstile site key: show the bot check on login / forgot forms */
  turnstileSiteKey: string | null;
}

export const getAuthOptions = () => api<AuthOptions>('/v1/auth/options', { quiet401: true });
export const forgotPassword = (email: string, turnstile?: string) =>
  api<{ ok: true; minutes: number }>('/v1/auth/forgot', { method: 'POST', body: { email, turnstile }, quiet401: true });
export const checkResetCode = (email: string, code: string, turnstile?: string) =>
  api<{ ok: true }>('/v1/auth/reset/check', { method: 'POST', body: { email, code, turnstile }, quiet401: true });
export const resetPassword = (email: string, code: string, password: string, turnstile?: string) =>
  api<{ ok: true }>('/v1/auth/reset', { method: 'POST', body: { email, code, password, turnstile }, quiet401: true });
export const changePassword = (currentPassword: string, newPassword: string) =>
  api<{ ok: true; otherDevicesSignedOut: number }>('/v1/me/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });

// ── Settings → Devices (PR B1) ──

export interface DeviceSession {
  id: string;
  /** "Chrome on Windows" */
  device: string;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  /** this browser */
  current: boolean;
}

export const getSessions = () => api<{ sessions: DeviceSession[] }>('/v1/me/sessions');
export const signOutSession = (id: string) =>
  api<{ ok: true; sipRotated: boolean }>(`/v1/me/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const signOutOthers = () =>
  api<{ ok: true; signedOut: number; sipRotated: boolean }>('/v1/me/sessions/sign-out-others', { method: 'POST' });

/** Would this new password be accepted? (strength meter; same rules as saving it) */
export const checkNewPassword = (password: string, email?: string) =>
  api<{ ok: true } | { ok: false; problem: string; message: string }>('/v1/auth/password-check', {
    method: 'POST',
    body: { password, ...(email ? { email } : {}) },
    quiet401: true,
  });

// ── Admin pages (admin PR 3a) ──

export interface AdminUser extends User {
  mbUserId: number | null;
  mbUsername: string | null;
  /** the numbers the server last saw on the account (digits) */
  numbers: string[];
  /** Numbers PR 2: the names of the named ones ({ number → name }) */
  numberNames?: Record<string, string>;
  /** PR 1b: credit + readiness as the worker last read them */
  readiness?: AdminReadiness;
  /** PR 2: plan minutes (null level = no free minutes the meter follows) */
  usage?: { level: UsageLevel | null; used: number | null; bundle: number | null };
  /** PR 3a: payments (chips Unpaid / Overdue) */
  billing?: { state: 'paid' | 'unpaid' | 'overdue'; owedCents: number; since: string | null };
}

export interface InviteStatus {
  /** open = the link works now; expired = it ran out; none = no invitation waiting */
  state: 'none' | 'open' | 'expired';
  /** the newest invitation, open or not */
  sentAt: string | null;
  until: string | null;
  sentBy: string | null;
  acceptedAt: string | null;
  endedAt: string | null;
  endedWhy: string | null;
}

export interface AdminUserDetail {
  user: AdminUser;
  twoStep: TwoStepStatus;
  /** this is the signed-in admin's own account */
  you: boolean;
  /** admin PR 3b: the newest invitation (null = never invited), whether one may be sent now, email set up */
  invite?: InviteStatus | null;
  canInvite?: boolean;
  emailOn?: boolean;
  /** PR 1b: admin → Settings "low credit" amount */
  lowCreditCents?: number;
}

/** What happened to an invitation email ('limit' = hourly cap, 'failed' = the mail server refused it). */
export interface InviteSent {
  until: string;
  mail: 'sent' | 'failed' | 'limit' | 'off';
}

export type TimelineCategory = 'account' | 'security' | 'login' | 'settings' | 'phone' | 'calls' | 'numbers' | 'plan' | 'note' | 'other';

export interface TimelineEntry {
  id: number;
  at: string;
  action: string;
  category: TimelineCategory;
  title: string;
  actor: { kind: 'user' | 'admin' | 'cli' | 'system' | 'visitor'; email?: string };
  ip: string | null;
  device?: string;
  note?: string;
  detail: Record<string, unknown>;
}

export interface ResetStatus {
  emailOn: boolean;
  paused: boolean;
  codesToday: number;
  lastCode: null | { sentAt: string; expiresAt: string; state: 'open' | 'expired' | 'ended'; wrongTries: number; maxTries: number; endedAt: string | null };
  lastResetAt: string | null;
}

export interface TempPasswordAnswer {
  tempPassword: string;
  tempPasswordUntil: string;
}

const q = (params: Record<string, string | number | undefined | null>) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const adminListUsers = (
  filter: { q?: string; status?: 'active' | 'disabled'; role?: 'admin' | 'user'; invited?: boolean; plan?: number | 'none'; notReady?: boolean; minutes?: 'low' | 'used_up'; unpaid?: boolean } = {},
  signal?: AbortSignal,
) => {
  const { invited, notReady, unpaid, ...rest } = filter;
  return api<{ users: AdminUser[] }>(`/v1/admin/users${q({ ...rest, ...(invited ? { invited: 'true' } : {}), ...(notReady ? { notReady: 'true' } : {}), ...(unpaid ? { unpaid: 'true' } : {}), limit: 500 })}`, { signal }).then(
    (r) => r.users,
  );
};
export const adminGetUser = (id: number) => api<AdminUserDetail>(`/v1/admin/users/${id}`);
/** New user: an invitation email (`invite: true`) or a temporary password shown once. */
export const adminCreateUser = (body: {
  email: string;
  name: string;
  role: 'admin' | 'user';
  phone: boolean;
  code?: string;
  invite: boolean;
  /** PR 1a: the plan, the summary the admin saw (refused 409 quote_changed if it moved), who referred them */
  planId?: number | null;
  planExpect?: string;
  referredBy?: number | null;
  referredNote?: string | null;
  /** PR 3a: the payment received (QuickBooks) — it must cover the plan's first payment */
  creditCents?: number;
  creditReference?: string | null;
}) =>
  api<
    Partial<TempPasswordAnswer> & {
      user: AdminUser;
      invite?: InviteSent;
      adminsEmailed?: { to: number; sent: number };
      planQuote?: Quote;
      planError?: string;
      creditAdded?: { cents: number; status: string };
      creditError?: string;
      referralError?: string;
    }
  >('/v1/admin/users', { method: 'POST', body });
/** Send (again) an invitation; an admin's needs a two-step code. */
export const adminSendInvite = (id: number, code?: string) =>
  api<{ ok: true; user: AdminUser; invite: InviteSent; again: boolean }>(`/v1/admin/users/${id}/invite`, { method: 'POST', body: code ? { code } : {} });
/** Cancel an invitation: the link stops working and the account is deactivated. */
export const adminCancelInvite = (id: number) =>
  api<{ ok: true; user: AdminUser; mbUpdated: boolean; mbError?: string }>(`/v1/admin/users/${id}/invite/cancel`, { method: 'POST', body: {} });
/** Numbers PR 2: an admin names one of a user's numbers (null or '' removes the name). */
export const adminSaveNumberName = (id: number, number: string, nickname: string | null) =>
  api<{ user: AdminUser; number: string; nickname: string | null }>(`/v1/admin/users/${id}/numbers/${encodeURIComponent(number.replace(/[^0-9]/g, ''))}`, {
    method: 'PATCH',
    body: { nickname },
  });
export const adminUpdateUser = (
  id: number,
  body: { status?: 'active' | 'disabled'; email?: string; name?: string; ringAll?: boolean; callerId?: string | null; code?: string; timeZone?: string | null },
) =>
  api<{ user: AdminUser; email?: { mbUpdated: boolean; mbError?: string; mailOld: string; mailNew: string; invite?: InviteSent | { error: string } }; mbError?: string; mbNameError?: string; sessionsEnded?: number }>(
    `/v1/admin/users/${id}`,
    { method: 'PATCH', body },
  );
export const adminTempPassword = (id: number, code?: string) =>
  api<TempPasswordAnswer & { sessionsEnded: number }>(`/v1/admin/users/${id}/temp-password`, { method: 'POST', body: code ? { code } : {} });
export const adminSetRole = (id: number, role: 'admin' | 'user', code: string) =>
  api<{ user: AdminUser; sessionsEnded: number; adminsEmailed: { to: number; sent: number } }>(`/v1/admin/users/${id}/role`, { method: 'POST', body: { role, code } });
export const adminAddPhone = (id: number) => api<{ user: User }>(`/v1/admin/users/${id}/phone`, { method: 'POST', body: {} });
export const adminRotateSip = (id: number) => api<{ user: User }>(`/v1/admin/users/${id}/rotate-sip`, { method: 'POST', body: {} });
export const adminSessions = (id: number) => api<{ sessions: DeviceSession[] }>(`/v1/admin/users/${id}/sessions`).then((r) => r.sessions);
export const adminSignOut = (id: number, sid: string) =>
  api<{ ok: true; signedOut: number }>(`/v1/admin/users/${id}/sessions/${encodeURIComponent(sid)}`, { method: 'DELETE' });
export const adminSignOutAll = (id: number) => api<{ ok: true; signedOut: number }>(`/v1/admin/users/${id}/sessions/sign-out-all`, { method: 'POST', body: {} });
export const adminResetStatus = (id: number) => api<{ reset: ResetStatus }>(`/v1/admin/users/${id}/reset`).then((r) => r.reset);
export const adminSendReset = (id: number) =>
  api<{ ok: boolean; mail: string; minutes: number; reset: ResetStatus }>(`/v1/admin/users/${id}/reset/send`, { method: 'POST', body: {} });
export const adminClearResetPause = (id: number) =>
  api<{ ok: true; wasPaused: boolean; reset: ResetStatus }>(`/v1/admin/users/${id}/reset/clear-pause`, { method: 'POST', body: {} });
export const adminTimeline = (id: number, opts: { before?: number | null; category?: TimelineCategory | null; until?: string | null; limit?: number } = {}) =>
  api<{ entries: TimelineEntry[]; nextBefore: number | null }>(
    `/v1/admin/users/${id}/timeline${q({ before: opts.before ?? undefined, category: opts.category ?? undefined, until: opts.until ?? undefined, limit: opts.limit ?? 100 })}`,
  );
export const adminAddNote = (id: number, text: string) => api<{ ok: true }>(`/v1/admin/users/${id}/notes`, { method: 'POST', body: { text } });

// ── Plans (PR 1a) ──

export type PlanCategory = 'individual' | 'team' | 'enterprise';
export interface PlanFeature {
  text: string;
  /** not built yet: "Coming soon" */
  soon: boolean;
}
export interface Plan {
  id: number;
  category: PlanCategory;
  code: string;
  name: string;
  priceCents: number;
  minutes: number;
  texts: number;
  /** dollars per minute after the bundle */
  overagePerMin: number;
  /** dollars per text after the allowance */
  textRate: number;
  numberCents: number;
  firstNumberIncluded: boolean;
  minUsers: number;
  features: PlanFeature[];
  sort: number;
  status: 'offered' | 'hidden';
  createdAt: string;
  updatedAt: string;
}
export type PlanEdit = Omit<Plan, 'id' | 'createdAt' | 'updatedAt'>;
/** The terms an account keeps (copied when the plan was assigned). */
export interface Terms {
  planId: number;
  code: string;
  name: string;
  category: PlanCategory;
  priceCents: number;
  minutes: number;
  texts: number;
  overagePerMin: number;
  textRate: number;
  numberCents: number;
  firstNumberIncluded: boolean;
  minUsers: number;
}
export interface Quote {
  kind: 'new' | 'upgrade' | 'downgrade' | 'switch' | 'remove' | 'cancel_change';
  todayCents: number;
  from: { y: number; m: number; d: number };
  lines: string[];
  text: string;
}
export interface PlanChange {
  id: number;
  at: string;
  action: 'plan.created' | 'plan.edited' | 'plan.deleted';
  title: string;
  detail: Record<string, unknown>;
  by: string | null;
}
export interface PlanBox {
  plan: null | {
    terms: Terms;
    since: string | null;
    order: { text: string; lines: string[]; todayCents: number; at: string } | null;
    prepaidUntil: string | null;
    catalog: { name: string; status: 'offered' | 'hidden'; priceCents: number } | null;
  };
  next: null | { terms: Terms; from: string };
  referral: { by: null | { id: number; name: string; email: string }; note: string | null };
  referred: Array<{ id: number; name: string; email: string; status: string; plan: string | null; since: string }>;
}
export interface AppSettings {
  billingTimeZone: string;
  lowCreditCents: number;
  lowCreditChangedAt: string | null;
  lowCreditChangedBy: string | null;
}
export interface MyPlan {
  plan: null | { terms: Terms; since: string | null; order: { text: string; lines: string[]; at: string } | null; features: PlanFeature[] };
  next: null | { name: string; priceCents: number; category: PlanCategory; from: string };
}

/** A 409 quote_changed answer: the summary as it is now (show it and ask again). */
export const changedQuote = (err: unknown): Quote | null =>
  err instanceof ApiError && err.code === 'quote_changed' ? ((err.data as { quote?: Quote } | undefined)?.quote ?? null) : null;

export const adminPlans = () => api<{ plans: Array<Plan & { users: number; waiting: number }>; changes: PlanChange[] }>('/v1/admin/plans');
export const adminCreatePlan = (body: PlanEdit) => api<{ plan: Plan }>('/v1/admin/plans', { method: 'POST', body });
export const adminUpdatePlan = (id: number, body: Partial<PlanEdit>) =>
  api<{ plan: Plan; changed: string[]; keepTerms: number }>(`/v1/admin/plans/${id}`, { method: 'PATCH', body });
export const adminDeletePlan = (id: number) => api<{ ok: true }>(`/v1/admin/plans/${id}`, { method: 'DELETE' });
/** The order summary: a new account (no userId) or a change; planId null = take the plan away. */
export const adminQuote = (planId: number | null, userId?: number) =>
  api<{ quote: Quote; today: string }>(`/v1/admin/plans/${planId ?? 'none'}/quote${userId ? `?user=${userId}` : ''}`).then((r) => r.quote);
export const adminUserPlan = (id: number) => api<PlanBox>(`/v1/admin/users/${id}/plan`);
export const adminSetPlan = (id: number, planId: number | null, expect?: string) =>
  api<PlanBox & { user: User; quote: Quote }>(`/v1/admin/users/${id}/plan`, { method: 'PUT', body: { planId, ...(expect !== undefined ? { expect } : {}) } });
export const adminSetReferral = (id: number, by: number | null, note: string | null) =>
  api<PlanBox & { user: User; changed: boolean }>(`/v1/admin/users/${id}/referred-by`, { method: 'PUT', body: { by, note } });
export const adminSettings = () => api<{ settings: AppSettings; today: string }>('/v1/admin/settings');
export const adminSaveSettings = (body: { lowCreditCents: number }) => api<{ settings: AppSettings }>('/v1/admin/settings', { method: 'PATCH', body });
export const getMyPlan = () => api<MyPlan>('/v1/me/plan');

// ── Readiness (PR 1b) ──

export type Readiness = 'ready' | 'no_number' | 'no_credit' | 'low_credit';
export interface MyReadiness {
  /** PR 2 adds the plan-minutes lines (80 % / used up) */
  line: { kind: Readiness | 'minutes_80' | 'minutes_used_up' | 'unpaid' | 'overdue'; tone: 'warn' | 'bad'; text: string } | null;
  /** PR 3b: calls are paused (payment overdue): don't start calls */
  paused?: boolean;
  numberReady: { numbers: string[] } | null;
}
/** What admins see (list chips, the Readiness box); null credit = not read yet. */
export interface AdminReadiness {
  state: Readiness | null;
  since: string | null;
  noPlan: boolean;
  credit: number | null;
  postpaid: boolean | null;
  creditLimit: number | null;
  freeMinutes: boolean | null;
  mbReminderOn: boolean | null;
  readAt: string | null;
}
// PR 2b: this device's zone, so an "Automatic" user's reset time is worded in it (a chosen zone wins on the server)
export const getReadiness = () => api<MyReadiness>(`/v1/me/readiness?tz=${encodeURIComponent(deviceZone())}`);
export const closeNumberReady = () => api<{ ok: true }>('/v1/me/readiness/number-seen', { method: 'POST', body: {} });

// ── Usage meter (PR 2) ──

export type UsageLevel = 'ok' | 'warn80' | 'used_up';
/** One period of the monthly report (planMinutes null = the Call Engine couldn't be read). */
export interface UsageMonth {
  title: string;
  from: string;
  planMinutes: number | null;
  callsOut: number;
  minutesOut: number;
  callsIn: number;
  minutesIn: number;
  current: boolean;
}
export interface UsageMeter {
  used: number;
  bundle: number;
  left: number;
  level: UsageLevel;
  resetAt: string;
  /** "Dec 1" or "Nov 30 at 7 PM", in the reader's zone */
  resetText: string;
}
export interface MyUsage {
  meter: UsageMeter | null;
  report: UsageMonth[];
}
export interface AdminUsage {
  hasOffer: boolean;
  /** PR 3a: the plan just changed and the Call Engine is being set (seconds) */
  syncing?: boolean;
  offer: null | {
    id: number | null;
    label: string;
    state: 'ok' | 'not_active' | 'unsupported';
    why: string | null;
    seconds: number;
    usedSeconds: number | null;
    from: string | null;
    resetAt: string | null;
    resetText: string | null;
  };
  meter: null | { used: number; bundle: number; left: number; level: UsageLevel; since: string | null };
  /** thisMonth (PR 3a): the minutes the plan gives this month (prorated first month, upgrades) */
  plan: null | { name: string; minutes: number; thisMonth?: number | null };
  issues: Array<{ kind: string; text: string }>;
  readAt: string | null;
  report: UsageMonth[];
}
export const getMyUsage = () => api<MyUsage>(`/v1/me/usage?tz=${encodeURIComponent(deviceZone())}`);
export const adminUserUsage = (id: number) => api<{ usage: AdminUsage }>(`/v1/admin/users/${id}/usage`).then((r) => r.usage);

// ── Wallet (PR 3a) ──────────────────────────────────────────

export interface WalletEntry {
  id: number;
  kind: 'topup' | 'correction' | 'plan_charge' | 'monthly_fee' | 'incoming_overage' | 'call_overage' | 'free_minutes_back';
  /** + added, − charged */
  cents: number;
  status: 'pending' | 'done' | 'owed' | 'failed' | 'written_off';
  reference: string | null;
  what: string | null;
  at: string;
  doneAt: string | null;
  refillId: number | null;
  error: string | null;
  by: string | null;
}
export interface AdminWallet {
  /** BILLING switch on the server */
  billing: boolean;
  /** available credit in cents (null = not read yet) */
  credit: number | null;
  readAt: string | null;
  /** YYYY-MM-DD: the last day the fees paid so far cover */
  paidThrough: string | null;
  owedCents: number;
  unpaidSince: string | null;
  payState: 'paid' | 'unpaid' | 'overdue';
  unpaidDay: number;
  entries: WalletEntry[];
  callEngine: { ownOfferId: number | null; syncedAt: string | null; error: string | null; allowance: number | null; allowanceMonth: string | null };
}
export const adminUserWallet = (id: number) => api<AdminWallet>(`/v1/admin/users/${id}/wallet`);
/** Why credit is taken off (session 29); the customer is told in plain words. */
export type CorrectionReason = 'refund' | 'reversed' | 'mistake' | 'other';
export const adminAddCredit = (
  id: number,
  body: { cents: number; reference: string | null; correction: boolean; reason?: CorrectionReason | null; note?: string | null; notify?: boolean },
) =>
  api<AdminWallet & { status: WalletEntry['status']; before: number; after: number | null }>(`/v1/admin/users/${id}/wallet`, { method: 'POST', body });
